// What has actually been booked on this order (AFRU), and the reversal.
//
// Two reasons this exists. First, the order screen showed planned hours only, so a mechanic
// returning to an order could not see that he — or a colleague — had already booked, and re-booked.
// Second, a mis-typed confirmation was permanent as far as the portal was concerned: 80 instead of
// 8 stood until a key user opened IW45 in SAP GUI, which is exactly the moment the portal exists to
// remove (PM code audit finding 08).
//
// Reversal is gated on canConfirm, the same right as booking — deliberately, and for the same
// reason the counter-reading reversal is: the person who typed the wrong number is the one who
// knows it is wrong, and a bad booking sitting there until someone senior is free costs more than
// letting them fix it. Every reversal is attributed and stays visible.
//
// SAP does not delete a confirmation: it posts a COUNTER-confirmation, so both rows survive and
// the hours net out. The list therefore shows reversals and reversed rows rather than hiding them —
// hiding either would make the numbers on screen stop adding up.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Undo2 } from 'lucide-react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { Card, Button } from '../ui';
import { listConfirmations, reverseConfirmation } from '../../api';

export default function ConfirmationsPanel({ user, orderId, role, canConfirm, onChanged }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // One idempotency key per reversal attempt, minted when the row is clicked and rotated only
  // after the server answers — a retry after a lost response must reuse it, or the ledger can
  // never replay and a second counter-confirmation is posted.
  const keyRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setRows(await listConfirmations(user, orderId, role));
      setError(null);
    } catch (e) {
      setError(e.message);
      setRows([]);
    }
  }, [user, orderId, role]);

  useEffect(() => { if (open && rows === null) load(); }, [open, rows, load]);

  const reverse = async (row) => {
    if (!keyRef.current) keyRef.current = (window.crypto?.randomUUID?.() || `rev-${Date.now()}-${Math.round(Math.random() * 1e9)}`);
    setBusyId(`${row.confirmationId}-${row.counter}`);
    setError(null);
    try {
      const res = await reverseConfirmation(user, orderId, row.confirmationId, row.counter, undefined, keyRef.current, role);
      if (res && res.ok === false) throw new Error(res.error || t('pm.conf.reverseFailed'));
      keyRef.current = null;
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message);
    }
    setBusyId(null);
  };

  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  return (
    <Card>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
        className={`flex items-center gap-2 w-full text-left text-sm font-semibold ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        {t('pm.conf.title')}
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          {rows === null && <p className={`text-sm ${sub}`}>{t('pm.common.loading')}</p>}
          {rows && rows.length === 0 && <p className={`text-sm ${sub}`}>{t('pm.conf.none')}</p>}

          {(rows || []).map((r) => {
            const id = `${r.confirmationId}-${r.counter}`;
            // A reversed row and a reversal row are both struck through: neither counts towards
            // the hours any more, and both must stay readable.
            const spent = r.reversed || r.isReversal;
            return (
              <div key={id} className={`flex items-center gap-3 text-sm ${spent ? 'opacity-60' : ''}`}>
                <div className="flex-1 min-w-0">
                  <p className={spent ? 'line-through' : ''}>
                    {r.actualWork} {r.workUnit} · {t('pm.detail.operations')} {r.operationNo}
                    {r.finalConfirmation ? ` · ${t('pm.detail.finalConfirmation')}` : ''}
                  </p>
                  <p className={`text-xs ${sub}`}>
                    {r.postingDate} · {r.enteredBy || '—'}
                    {r.isReversal ? ` · ${t('pm.conf.isReversal')}` : ''}
                    {r.reversed ? ` · ${t('pm.conf.reversed')}` : ''}
                  </p>
                </div>
                {canConfirm && !spent && (
                  <Button variant="secondary" className="!w-auto !min-h-[40px] !text-sm flex-shrink-0"
                    disabled={busyId === id} onClick={() => reverse(r)}>
                    <Undo2 size={16} /> {busyId === id ? t('pm.conf.reversing') : t('pm.conf.reverse')}
                  </Button>
                )}
              </div>
            );
          })}

          {error && <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{error}</p>}
        </div>
      )}
    </Card>
  );
}
